import { db, type StoredPdf } from './db'
import type { CatalogCard } from '@/types/card'

/**
 * Verwaltung der offline vorgehaltenen PDFs.
 *
 * Ablauf beim Öffnen einer Karte: erst lokal nachsehen, nur bei Bedarf laden. Jeder
 * Download vermerkt den Katalog-Hash, sodass ein späterer Katalog sofort erkennen
 * lässt, welche Karten überholt sind.
 */

export type CardOfflineState = 'aktuell' | 'veraltet' | 'fehlt'

export interface DownloadProgress {
  /** Bereits geladene Karten. */
  done: number
  total: number
  /** Titel der Karte, die gerade geladen wird. */
  current: string
}

/** Stellt den Inhalt einer Karte bereit — aus dem lokalen Bestand oder aus dem Netz. */
export async function getPdf(card: CatalogCard, baseUrl: string): Promise<ArrayBuffer> {
  const stored = await db.pdfs.get(card.id)
  if (stored && stored.hash === card.pdfHash) {
    // Zugriffszeitpunkt fortschreiben, damit häufig genutzte Karten bei Speichermangel
    // als Letzte verdrängt werden.
    void db.pdfs.update(card.id, { lastUsedAt: Date.now() })
    return stored.data
  }
  return downloadPdf(card, baseUrl)
}

/** Lädt eine Karte herunter und legt sie lokal ab. */
export async function downloadPdf(card: CatalogCard, baseUrl: string): Promise<ArrayBuffer> {
  const response = await fetch(new URL(card.pdfPath, baseUrl))
  if (!response.ok) {
    throw new Error(`Karte „${card.title}" konnte nicht geladen werden (HTTP ${response.status}).`)
  }
  const data = await response.arrayBuffer()

  const entry: StoredPdf = {
    id: card.id,
    data,
    hash: card.pdfHash,
    bytes: data.byteLength,
    storedAt: Date.now(),
    lastUsedAt: Date.now(),
  }

  try {
    await db.pdfs.put(entry)
  } catch (error) {
    if (isQuotaError(error)) {
      await evictLeastRecentlyUsed(data.byteLength)
      await db.pdfs.put(entry)
    } else {
      throw error
    }
  }

  return data
}

export async function removePdf(id: string): Promise<void> {
  await db.pdfs.delete(id)
}

/** Zustand aller Karten des Katalogs: aktuell, veraltet oder gar nicht vorhanden. */
export async function getOfflineStates(
  cards: readonly CatalogCard[],
): Promise<Map<string, CardOfflineState>> {
  const stored = await db.pdfs.toArray()
  const byId = new Map(stored.map((entry) => [entry.id, entry]))
  return new Map(
    cards.map((card) => {
      const entry = byId.get(card.id)
      if (!entry) return [card.id, 'fehlt' as const]
      return [card.id, entry.hash === card.pdfHash ? ('aktuell' as const) : ('veraltet' as const)]
    }),
  )
}

/** Belegter Speicher in Byte. */
export async function usedBytes(): Promise<number> {
  const all = await db.pdfs.toArray()
  return all.reduce((sum, entry) => sum + entry.bytes, 0)
}

/** Vom Browser zugestandenes Kontingent — für die Anzeige in den Einstellungen. */
export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  if (!navigator.storage?.estimate) return null
  const { usage = 0, quota = 0 } = await navigator.storage.estimate()
  return { usage, quota }
}

/**
 * Bittet den Browser, die Daten dauerhaft zu behalten. Ohne diese Zusage darf er den
 * Speicher bei Platzmangel jederzeit räumen — und dann fehlen die Karten genau dann,
 * wenn sie gebraucht werden.
 */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false
  if (await navigator.storage.persisted?.()) return true
  return navigator.storage.persist()
}

/**
 * Lädt alle fehlenden oder veralteten Karten nacheinander.
 * Nacheinander statt parallel: Das schont eine schwache Mobilfunkverbindung und
 * lässt den Fortschritt ehrlich anzeigen.
 */
export async function downloadAll(
  cards: readonly CatalogCard[],
  baseUrl: string,
  onProgress: (progress: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<{ loaded: number; failed: { title: string; reason: string }[] }> {
  const states = await getOfflineStates(cards)
  const pending = cards.filter((card) => states.get(card.id) !== 'aktuell')

  let done = 0
  const failed: { title: string; reason: string }[] = []

  for (const card of pending) {
    if (signal?.aborted) break
    onProgress({ done, total: pending.length, current: card.title })
    try {
      await downloadPdf(card, baseUrl)
      done++
    } catch (error) {
      failed.push({ title: card.title, reason: (error as Error).message })
    }
  }

  onProgress({ done, total: pending.length, current: '' })
  return { loaded: done, failed }
}

function isQuotaError(error: unknown): boolean {
  return error instanceof DOMException && (error.name === 'QuotaExceededError' || error.code === 22)
}

/**
 * Schafft Platz, indem die am längsten nicht benutzten Karten entfernt werden.
 * Favoriten bleiben dabei unangetastet — sie sind genau die Karten, die jemand
 * bewusst griffbereit haben wollte.
 */
async function evictLeastRecentlyUsed(needed: number): Promise<void> {
  const favorites = new Set((await db.favorites.toArray()).map((f) => f.id))
  const candidates = (await db.pdfs.orderBy('lastUsedAt').toArray()).filter(
    (entry) => !favorites.has(entry.id),
  )

  let freed = 0
  for (const entry of candidates) {
    if (freed >= needed * 1.5) break
    await db.pdfs.delete(entry.id)
    freed += entry.bytes
  }

  if (freed === 0) {
    throw new Error(
      'Der Gerätespeicher ist voll und es lassen sich keine Karten freigeben. ' +
        'Bitte in den Einstellungen nicht benötigte Karten entfernen.',
    )
  }
}
